import axios, { AxiosInstance } from 'axios';
import { ApiResponse } from 'types';
import { WIDGETS_SDK_API_ERROR_CODES, WidgetsSdkEncKeyPairResponse } from 'types/apis/widgetSdkInterfaces';

export const CxWebEncryptionApis = {
  /**
   * Get encryption key pair from a specific dataserver URL
   * @param dataserverUrl - The base URL of the dataserver (e.g., 'https://api-server-blackcreek-dev.bidgely.com' or 'api-server-blackcreek-dev.bidgely.com')
   * @param clientId - The client ID (e.g., 'blackcreek-dashboard')
   * @param accessToken - The access token for authentication
   * @returns Promise with encryption key pair (iv and aesKey) or error
   */
  getWidgetsSdkEncKeyPair: async (
    dataserverUrl: string,
    clientId: string,
    accessToken: string
  ): Promise<ApiResponse<WidgetsSdkEncKeyPairResponse>> => {
    // Create a temporary axios instance for this specific dataserver URL
    const tempAxiosClient: AxiosInstance = axios.create();

    // Set the Authorization header for all requests
    tempAxiosClient.interceptors.request.use((config) => {
      config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    });

    // Ensure dataserverUrl has protocol, but don't double-add it
    const baseUrl = dataserverUrl.startsWith('http://') || dataserverUrl.startsWith('https://')
      ? dataserverUrl
      : `https://${dataserverUrl}`;

    try {
      // First, try to GET the encryption key
      const resp = await tempAxiosClient.get<{
        requestId?: string;
        payload?: WidgetsSdkEncKeyPairResponse;
        error?: any;
      }>(`${baseUrl}/v2.0/encryption/key/${clientId}`);

      // Check for error in response
      if (resp.data.error) {
        return {
          error: {
            code: resp.data.error.code || 'UNKNOWN_ERROR',
            message: resp.data.error.message || 'Error from API',
          },
        };
      }

      // Extract data from payload property (API response structure: { requestId, payload: { iv, aesKey, ... }, error })
      const keyPairData = resp.data.payload;

      // Validate response structure
      if (!keyPairData || !keyPairData.iv || !keyPairData.aesKey) {
        return {
          error: {
            code: 'INVALID_RESPONSE',
            message: 'Invalid response structure: missing iv or aesKey in payload',
          },
        };
      }

      return { data: keyPairData };
    } catch (error: any) {
      // Check if the error is ERROR_ENCRYPTION_KEY_NOT_FOUND
      if (error.response?.data?.error?.code === WIDGETS_SDK_API_ERROR_CODES.ERROR_ENCRYPTION_KEY_NOT_FOUND) {
        // Try to POST to create the encryption key
        try {
          const keyPairResp = await tempAxiosClient.post<{
            requestId?: string;
            payload?: WidgetsSdkEncKeyPairResponse;
            error?: any;
          }>(
            `${baseUrl}/v2.0/encryption/key`,
            { clientId },
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          // Check for error in response
          if (keyPairResp.data.error) {
            return {
              error: {
                code: keyPairResp.data.error.code || 'UNKNOWN_ERROR',
                message: keyPairResp.data.error.message || 'Error creating encryption key pair',
              },
            };
          }

          // Extract data from payload property
          const keyPairData = keyPairResp.data.payload;

          if (keyPairData && keyPairData.iv && keyPairData.aesKey) {
            return { data: keyPairData };
          } else {
            return {
              error: {
                code: 'UNKNOWN_ERROR',
                message: 'Failed to create encryption key pair - invalid response structure',
              },
            };
          }
        } catch (postError: any) {
          return {
            error: {
              code: postError.response?.data?.error?.code || 'UNKNOWN_ERROR',
              message:
                postError.response?.data?.error?.message ||
                postError.message ||
                'Failed to create encryption key pair',
            },
          };
        }
      }

      // Return the original error
      return {
        error: {
          code: error.response?.data?.error?.code || 'UNKNOWN_ERROR',
          message:
            error.response?.data?.error?.message || error.message || 'Failed to get encryption key pair',
        },
      };
    }
  },
};

